import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, X } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TrainingDiaryModal = ({ session, isOpen, onClose, onUpdate, onSwitchMode, mode }) => {
  const { addToast } = useToast();
  const [formData, setFormData] = useState({
    weapon_type: '',
    result_description: '',
    training_notes: ''
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Weapon type options
  const weaponTypes = [
    'Velg type...',
    'Pistol',
    'Revolver', 
    'Rifle',
    'Luftpistol',
    'Luftrifle',
    'Dynamisk',
    'Feltskyting',
    'Annet'
  ];

  useEffect(() => {
    if (session && isOpen) {
      try {
        // Pre-populate form with existing data
        setFormData({
          weapon_type: session.weapon_type || '',
          result_description: session.result_description || '',
          training_notes: session.training_notes || ''
        });
        
        // Load existing images - prefer S3/CloudFront URLs over Cloudinary URLs
        // This ensures migrated images are served from S3 for better performance
        const s3Images = session.target_images_s3_urls || [];
        const originalImages = session.target_images || [];
        
        // Use S3 URLs if available, otherwise fall back to original URLs
        // Filter out any null/undefined values
        const displayImages = (s3Images.length > 0 ? s3Images : originalImages).filter(Boolean);
        setImages(displayImages);
      } catch (error) {
        console.error('Error loading diary data:', error);
        setFormData({
          weapon_type: '',
          result_description: '',
          training_notes: ''
        });
        setImages([]);
      }
    }
  }, [session, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        addToast('Kun bildefiler er tillatt', 'error');
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
        addToast(
          `Bildet er for stort (${fileSizeMB}MB). Maksimal størrelse er 10MB. ` +
          `Tips: Reduser bildestørrelsen på telefonen før opplasting, eller bruk en bildekomprimeringsapp.`,
          'error',
          8000
        );
        continue;
      }
      
      setUploading(true);
      
      try {
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const response = await axios.post(
          `${API_BASE}/api/training/sessions/${session.id}/upload-image`,
          { image_base64: base64Data },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          setImages(prev => [...prev, response.data.image_url]);
          addToast('Bilde lastet opp og behandlet', 'success');
        }
      } catch (error) {
        console.error('Image upload error:', error);
        addToast('Kunne ikke laste opp bildet', 'error');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    if (confirm('Er du sikker på at du vil slette dette bildet?')) {
      setImages(prev => prev.filter((_, index) => index !== indexToRemove));
      addToast('Bilde slettet', 'success');
    }
  };

  const handleDownloadImage = async (image, index) => {
    try {
      const filename = `skytedagbok_bilde_${index + 1}_${new Date().toISOString().split('T')[0]}.jpg`;
      
      if (image.startsWith('http')) {
        // For Cloudinary URLs, fetch and download
        const response = await fetch(image);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // For legacy base64 images
        const link = document.createElement('a');
        link.href = `data:image/jpeg;base64,${image}`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      addToast('Bilde lastet ned', 'success');
    } catch (error) {
      console.error('Download error:', error);
      addToast('Kunne ikke laste ned bildet', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(
        `${API_BASE}/api/training/sessions/${session.id}/diary`,
        {
          weapon_type: formData.weapon_type === 'Velg type...' ? null : formData.weapon_type,
          result_description: formData.result_description,
          training_notes: formData.training_notes,
          target_images: images
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      addToast('Skytedagbok oppdatert!', 'success');
      onUpdate(); // Refresh the training log
      onClose();
      
    } catch (error) {
      console.error('Update diary error:', error);
      addToast('Kunne ikke oppdatere skytedagbok', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-gray-800">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-medium text-white">
                📖 Skytedagbok
              </h3>
              {onSwitchMode && (
                <>
                  <button
                    type="button"
                    onClick={() => onSwitchMode('pro')}
                    className="px-3 py-1 rounded-full text-[10px] font-extrabold tracking-widest uppercase"
                    style={{
                      background: mode === 'pro' ? '#FFD700' : '#374151',
                      color: mode === 'pro' ? '#030712' : '#94A3B8',
                    }}
                    data-testid="mode-pill-pro"
                    title="PRO-modus (avansert)"
                  >
                    Pro
                  </button>
                  <button
                    type="button"
                    onClick={() => onSwitchMode('enkel')}
                    className="px-3 py-1 rounded-full text-[10px] font-extrabold tracking-widest uppercase"
                    style={{
                      background: mode === 'enkel' ? '#FFD700' : '#374151',
                      color: mode === 'enkel' ? '#030712' : '#94A3B8',
                    }}
                    data-testid="mode-pill-enkel"
                    title="Enkel modus"
                  >
                    Enkel
                  </button>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl font-bold"
            >
              ×
            </button>
          </div>
          
          {/* Session Info */}
          <div className="mb-6 p-4 bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-300">
              <strong>Trening:</strong> {session?.activity || 'Ikke spesifisert'}
            </p>
            <p className="text-sm text-gray-300">
              <strong>Dato:</strong> {session?.start_time ? new Date(session.start_time).toLocaleDateString('no-NO') : 'Ukjent dato'}
            </p>
            <p className="text-sm text-gray-300">
              <strong>Lokasjon:</strong> {session?.location_name || 'Ikke spesifisert'}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Weapon Type Dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Våpentype
              </label>
              <select
                name="weapon_type"
                value={formData.weapon_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                {weaponTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Result Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Hvordan gikk treningen? (Resultater)
              </label>
              <textarea
                name="result_description"
                value={formData.result_description}
                onChange={handleInputChange}
                rows={3}
                placeholder="Beskriv hvordan økta gikk, resultater, måloppnåelse..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            {/* Training Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Personlige notater
              </label>
              <textarea
                name="training_notes"
                value={formData.training_notes}
                onChange={handleInputChange}
                rows={4}
                placeholder="Dine egne notater om treningen, teknikk, erfaringer..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            {/* Image Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Last opp bilder av resultater
              </label>
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-yellow-500 file:text-gray-900 hover:file:bg-yellow-400"
                  disabled={uploading}
                />
                <p className="text-xs text-gray-400 mt-1">
                  PNG, JPG, GIF opp til 10MB. Bilder komprimeres automatisk til 1200px bredde og 72 DPI.
                </p>
              </div>

              {/* Image Preview */}
              {images.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.startsWith('http') ? image : `data:image/jpeg;base64,${image}`}
                        alt={`Resultat ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-600"
                      />
                      
                      {/* Action buttons */}
                      <div className="absolute top-2 right-2 flex space-x-1">
                        {/* Download button */}
                        <button
                          type="button"
                          onClick={() => handleDownloadImage(image, index)}
                          className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-green-600 transition-colors"
                          title="Last ned bilde"
                        >
                          <Download className="h-3 w-3" />
                        </button>
                        
                        {/* Delete button */}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                          title="Slett bilde"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={loading || uploading}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-400 disabled:opacity-50"
              >
                {loading ? 'Lagrer...' : 'Lagre skytedagbok'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrainingDiaryModal;